import type { PointerEvent as ReactPointerEvent } from 'react';
import { displayName } from '@/fs/icons';
import type { FsNode } from '@/fs/types';
import { clearDrag, useDragStore } from './store';
import { isValidDrop, targetAt } from './drop';
import type { DropPlacement, DropTarget } from './drop';

/**
 * One file drag, from the press that might become one to the drop that ends it.
 *
 * Written as a function rather than a hook because that is how the gesture
 * actually reads at the call site — `showContextMenu(event, items)` next door
 * takes the same shape — and because a drag outlives the component that
 * started it: the icon can be re-rendered, moved or unmounted while the
 * pointer is still down, and none of the state below cares.
 */

/** XP's own drag threshold (SM_CXDRAG): a slip of the hand is still a click. */
const THRESHOLD = 4;

export interface DragSourceItem {
  node: FsNode;
  icon: string;
  /** Size the surface draws the icon at, so the ghost matches it. */
  iconSize: number;
  /** The element it was picked up from, which sets where its ghost rides. */
  el: HTMLElement | null;
}

export interface ItemDragOptions {
  items: readonly DragSourceItem[];
  /** Identifies the surface, so it can tell its own drops from an import. */
  owner: string;
  /** A drag that ended somewhere. `target` is null over bare nothing. */
  onDrop(target: DropTarget | null, places: DropPlacement[]): void;
  /** Pointer released without ever crossing the threshold — a plain click. */
  onTap?(): void;
}

/** The element currently lit up as the drop target, if any. */
let hovered: HTMLElement | null = null;

function highlight(el: HTMLElement | null): void {
  if (hovered === el) return;
  hovered?.removeAttribute('data-drop-over');
  hovered = el;
  hovered?.setAttribute('data-drop-over', '');
}

export function startItemDrag(event: ReactPointerEvent, options: ItemDragOptions): void {
  if (event.button !== 0) return;
  if (options.items.length === 0) return;

  const startX = event.clientX;
  const startY = event.clientY;
  const pointerId = event.pointerId;
  const nodes = options.items.map((item) => item.node);

  // Measured now, while everything is still where the user sees it.
  const ghosts = options.items.map((item) => {
    const rect = item.el?.getBoundingClientRect();
    return {
      path: item.node.path,
      icon: item.icon,
      label: displayName(item.node),
      w: rect?.width ?? item.iconSize,
      iconSize: item.iconSize,
      // A cell much wider than it is tall wears its label beside the icon;
      // one roughly square wears it underneath. That covers Explorer's four
      // layouts and the desktop without any of them having to say so.
      inline: rect ? rect.width > rect.height * 1.6 : false,
      dx: (rect?.left ?? startX) - startX,
      dy: (rect?.top ?? startY) - startY,
    };
  });

  let dragging = false;
  let target: DropTarget | null = null;
  let frame: number | null = null;
  let pending: { x: number; y: number; valid: boolean } | null = null;
  let prevUserSelect = '';

  const flush = () => {
    frame = null;
    if (!pending) return;
    const { x, y, valid } = pending;
    pending = null;
    useDragStore.setState({ x, y, valid });
  };

  const begin = () => {
    dragging = true;
    prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    // `global.css` reads this to take the iframes out of the pointer's way,
    // which a window drag does with a shield over the whole desktop instead.
    // A file drag cannot use the shield: it has to hit-test the folders
    // underneath, and a sheet over everything is the one thing that stops it.
    document.body.setAttribute('data-dragging', 'true');
    useDragStore.setState({ active: true, ghosts, x: startX, y: startY, valid: false });
  };

  const track = (x: number, y: number) => {
    target = targetAt(x, y);
    const valid = target !== null && isValidDrop(target, nodes, options.owner);
    highlight(valid && target ? target.el : null);
    if (!valid) target = null;
    document.body.toggleAttribute('data-drag-refused', !valid);
    pending = { x, y, valid };
    if (frame === null) frame = requestAnimationFrame(flush);
  };

  const teardown = () => {
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', onUp);
    document.removeEventListener('pointercancel', onCancel);
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('blur', onCancel);

    if (frame !== null) {
      cancelAnimationFrame(frame);
      frame = null;
    }
    if (!dragging) return;

    highlight(null);
    document.body.style.userSelect = prevUserSelect;
    document.body.removeAttribute('data-dragging');
    document.body.removeAttribute('data-drag-refused');
    clearDrag();
  };

  function onMove(e: PointerEvent) {
    if (e.pointerId !== pointerId) return;
    if (!dragging) {
      if (Math.abs(e.clientX - startX) < THRESHOLD && Math.abs(e.clientY - startY) < THRESHOLD) {
        return;
      }
      begin();
    }
    track(e.clientX, e.clientY);
  }

  function onUp(e: PointerEvent) {
    if (e.pointerId !== pointerId) return;
    const dropped = dragging ? target : null;
    const at = { x: e.clientX, y: e.clientY };
    const wasDragging = dragging;
    teardown();

    if (!wasDragging) {
      options.onTap?.();
      return;
    }
    options.onDrop(
      dropped,
      ghosts.map((ghost) => ({ path: ghost.path, x: at.x + ghost.dx, y: at.y + ghost.dy })),
    );
  }

  function onCancel() {
    teardown();
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key !== 'Escape') return;
    // Escape abandons the drag and nothing else: the window underneath must
    // not read it as a request to close.
    e.preventDefault();
    e.stopPropagation();
    teardown();
  }

  document.addEventListener('pointermove', onMove);
  document.addEventListener('pointerup', onUp);
  document.addEventListener('pointercancel', onCancel);
  window.addEventListener('keydown', onKeyDown, true);
  window.addEventListener('blur', onCancel);
}
